/**
 * F29 (Wave 2C) — DisbandTeamDialog.
 *
 * Terminal destructive action. Type-to-confirm phrase "DISBAND" + mandatory
 * reason (Textarea, 1–500 chars per `DisbandTeamByAdminInputSchema`). Shows a
 * bulleted consequence list emphasising irreversibility.
 *
 * Same composition pattern as `SuspendTeamDialog` (Modal + react-hook-form +
 * inline FormField, NOT the F02 `SuspendDialog` primitive — F29 D11 wants
 * free-form reason text saved into the member-visible audit log).
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, TextInput, Textarea } from '@bravemove/ui-web';
import { DisbandTeamByAdminInputSchema, type DisbandTeamByAdminInput } from '@bravemove/types';

export interface DisbandTeamDialogProps {
  readonly open: boolean;
  readonly teamName: string;
  readonly loading: boolean;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (body: DisbandTeamByAdminInput) => void;
}

interface DisbandFormValues {
  readonly reason: string;
}

export function DisbandTeamDialog({
  open,
  teamName,
  loading,
  serverError,
  onClose,
  onSubmit,
}: DisbandTeamDialogProps) {
  const { t } = useI18n();
  const CONFIRM_PHRASE = t('admin:team.admin.force_disband_dialog.confirm_phrase');

  const localSchema = z.object({
    reason: DisbandTeamByAdminInputSchema.shape.reason,
  });

  const form = useForm<DisbandFormValues>({
    resolver: zodResolver(localSchema),
    defaultValues: { reason: '' },
    mode: 'onTouched',
  });

  const [typed, setTyped] = useState('');

  // Reset state on dialog (re)open — intentional setState-in-effect "form
  // reset on dialog open" pattern (mirrors F02 SuspendDialog primitive).
  useEffect(() => {
    if (open) {
      form.reset({ reason: '' });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTyped('');
    }
  }, [open, form]);

  const phraseMatches = typed.trim() === CONFIRM_PHRASE;
  const canConfirm = phraseMatches && !loading;

  const handleConfirm = () => {
    if (!canConfirm) return;
    void form.handleSubmit((v) => onSubmit({ reason: v.reason }))();
  };

  // ICU `{teamName}` interpolation does NOT fire reliably in test env (vitest
  // jsdom — same trap F23's CommissionStatementDetailPage hit). Resolve the
  // title manually here and pass the result string to <Modal>.
  const interpolated = t('admin:team.admin.force_disband_dialog.title', {
    teamName,
    defaultValue: 'Force-disband {teamName}?',
  }) as string;
  const titleText = interpolated.includes('{teamName}')
    ? `Force-disband ${teamName}?`
    : interpolated;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={titleText}
      description={t('admin:team.admin.force_disband_dialog.body')}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t('admin:team.admin.force_disband_dialog.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm}
            loading={loading}
            data-testid="disband-team-confirm"
          >
            {t('admin:team.admin.force_disband_dialog.confirm_button')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}
        className="flex flex-col gap-4"
      >
        <div className="rounded-lg border border-danger/30 bg-danger/5 p-3">
          <p className="text-[10.5px] font-mono uppercase tracking-wider text-danger font-bold mb-2">
            {t('admin:team.admin.force_disband_dialog.consequences_heading')}
          </p>
          <ul className="list-disc ps-5 space-y-1 text-[13px] text-ink">
            <li>{t('admin:team.admin.force_disband_dialog.consequence_terminal')}</li>
            <li>{t('admin:team.admin.force_disband_dialog.consequence_members_removed')}</li>
            <li>{t('admin:team.admin.force_disband_dialog.consequence_team_archived')}</li>
          </ul>
        </div>

        <FormField
          label={t('admin:team.admin.force_disband_dialog.reason_label')}
          helper={t('admin:team.admin.force_disband_dialog.reason_helper')}
          error={form.formState.errors.reason?.message}
          required
        >
          <Textarea
            rows={4}
            maxLength={500}
            showCounter
            data-testid="disband-team-reason"
            {...form.register('reason')}
          />
        </FormField>

        <FormField
          label={(() => {
            const raw = t('admin:team.admin.force_disband_dialog.type_to_confirm_label', {
              phrase: CONFIRM_PHRASE,
              defaultValue: 'Type {phrase} to confirm',
            }) as string;
            return raw.includes('{phrase}') ? `Type ${CONFIRM_PHRASE} to confirm` : raw;
          })()}
        >
          <TextInput
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            invalid={typed.length > 0 && !phraseMatches}
            autoComplete="off"
            data-testid="disband-team-confirm-phrase"
            aria-label={t('admin:team.admin.force_disband_dialog.type_to_confirm_label', {
              phrase: CONFIRM_PHRASE,
              defaultValue: 'Type {phrase} to confirm',
            })}
          />
        </FormField>

        {serverError ? <Banner tone="danger">{serverError}</Banner> : null}
      </form>
    </Modal>
  );
}
